import { AntDesign } from "@expo/vector-icons";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  FlatListSearchable,
  Page,
  Text,
  View,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity } from "react-native";
import {
  loadRecentProducts,
  RecentProduct,
} from "../../../../utilities/recent-products";
import { BarcodeComponent, ProductListItem } from "../../../../components";
import { PhotoImageRootParamList } from "../../../../types";
import { keys } from "../../../../utilities/i18n";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { productsQuery } from "../../../../utilities/product/constants";
import { useSearch } from "../../../../utilities/search";
import { useScheduler } from "../../../../utilities/use-scheduler";
import { useImagesSentSuccessProvider } from "../../components/ImagesSentSuccessProvider/hooks";
import { useSelectUploadImageProvider } from "../../components/SelectUploadImageProvider/hooks";
import { useFocusEffect } from "@react-navigation/native";

export type SelectProductForUtilityScreenProps = NativeStackScreenProps<
  PhotoImageRootParamList,
  "SelectProductForUtilityScreen"
>;

export const SelectProductForUtilityScreen = ({
  route,
  navigation,
}: SelectProductForUtilityScreenProps) => {
  const { t } = useTranslation();
  const options = route.params?.options;
  const search = useSearch();
  const { schedule } = useScheduler(500);
  const { spacings, colors } = useThemeProvider();
  const [needsPhotosOnly, setNeedsPhotosOnly] = useState(false);
  const [recentProducts, setRecentProducts] = useState<RecentProduct[]>([]);

  const { updateHandlers: updateSelectUploadImageHandlers } =
    useSelectUploadImageProvider();
  const { updateHandlers: updateImagesSentSuccessHandlers } =
    useImagesSentSuccessProvider();

  const loadMoreList = useLoadMoreList<Product>({
    query: productsQuery,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    initialLoad: true,
    variableFormatter: (variables) => {
      return {
        options: {
          ...variables,
          filter: {
            ...variables.filter,
            internal: { eq: false },
            ...(needsPhotosOnly ? { hasImages: { eq: false } } : {}),
          },
        },
      };
    },
  });

  const handleLoadMore = useCallback(
    (reset?: boolean) => {
      loadMoreList.loadMore(
        {
          filter: {
            term: { contains: search.query },
            internal: { eq: false },
            ...(needsPhotosOnly ? { hasImages: { eq: false } } : {}),
          },
        },
        reset
      );
    },
    [search.query, needsPhotosOnly, loadMoreList.loadMore]
  );

  function openRetailerCaptureScreen() {
    navigation.navigate("SelectUploadRetailerImageMethodScreen", {
      sku: search.query,
      options,
    });
  }

  const renderItem = useCallback(({ item }: { item: Product }) => {
    return (
      <ProductListItem
        product={item as any}
        action={() => {
          navigation.navigate("ProductImageDetailsRootScreen", {
            screen: "ProductImageDetailsScreen",
            productId: item.id,
          } as any);
        }}
      />
    );
  }, []);

  useEffect(() => {
    if (search.touched) {
      schedule(() => {
        handleLoadMore(true);
      });
    }
  }, [search.query, search.touched]);

  useEffect(() => {
    handleLoadMore(true);
  }, [needsPhotosOnly]);

  useFocusEffect(
    useCallback(() => {
      loadRecentProducts().then(setRecentProducts);

      updateSelectUploadImageHandlers({
        onUpload: () => undefined,
      });

      updateImagesSentSuccessHandlers({
        onTakeMore: (nav) => nav.goBack(),
        onNewProduct: (nav) => nav.pop(3),
        onFinished: (nav) => nav.pop(4),
      });
    }, [updateSelectUploadImageHandlers, updateImagesSentSuccessHandlers])
  );

  const listEmptyComponent =
    search.query && !loadMoreList.loading ? (
      <TouchableOpacity
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
        }}
        onPress={openRetailerCaptureScreen}
      >
        <View flex>
          <View>
            <Text
              variant="heading"
              fontWeight="medium"
              numberOfLines={1}
              ellipsizeMode="head"
            >
              {t(keys.noProductFoundUpload, { sku: search.query })}
            </Text>
            <Text variant="heading3" color="subdued">
              {t(keys.stockCodeWarning)}
            </Text>
          </View>
        </View>
        <AntDesign
          style={[{ marginLeft: spacings.s4 }]}
          name="right"
          size={24}
          color="red"
        />
      </TouchableOpacity>
    ) : null;

  function handleNavigateToBarcodeScanner() {
    navigation.navigate("BarcodeScannerRootScreen", {
      screen: "BarcodeScannerScreen",
    });
  }

  const rightMarkup = !search.query && (
    <BarcodeComponent onPress={handleNavigateToBarcodeScanner} />
  );

  const recentProductsMarkup =
    recentProducts.length > 0 && !search.query ? (
      <View style={{ marginTop: spacings.s3 }}>
        <Text
          variant="heading5"
          color="subdued"
          style={{ marginBottom: spacings.s2 }}
        >
          Recently edited
        </Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ marginBottom: spacings.s2 }}
        >
          {recentProducts.map((p) => (
            <TouchableOpacity
              key={p.id}
              onPress={() =>
                navigation.navigate("ProductImageDetailsRootScreen", {
                  screen: "ProductImageDetailsScreen",
                  productId: p.id,
                } as any)
              }
              style={{
                paddingHorizontal: spacings.s4,
                paddingVertical: spacings.s2,
                borderRadius: 16,
                borderWidth: 1,
                borderColor: colors.borderColor,
                marginRight: spacings.s2,
              }}
            >
              <Text variant="heading5" numberOfLines={1}>
                {p.name}
              </Text>
              <Text variant="heading5" color="subdued" numberOfLines={1}>
                {p.stockNumber}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    ) : null;

  const filterChipMarkup = (
    <TouchableOpacity
      onPress={() => setNeedsPhotosOnly((v) => !v)}
      style={{
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "flex-start",
        paddingHorizontal: spacings.s4,
        paddingVertical: spacings.s2,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: needsPhotosOnly ? colors.primary : colors.borderColor,
        backgroundColor: needsPhotosOnly ? colors.primary : "transparent",
        marginTop: spacings.s3,
        marginBottom: spacings.s2,
      }}
    >
      <Text
        variant="heading5"
        style={{ color: needsPhotosOnly ? "#fff" : colors.text }}
      >
        Needs photos
      </Text>
    </TouchableOpacity>
  );

  return (
    <Page>
      <FlatListSearchable
        rightComponent={rightMarkup}
        placeholder={t(keys.searchProducts)}
        data={loadMoreList.items || []}
        selectTextOnFocus={true}
        renderItem={renderItem}
        value={search.query}
        onChange={search.setQuery}
        ListEmptyComponent={listEmptyComponent}
        renderSearchBarHeader={(searchMarkup, dividerMarkup) => (
          <View>
            {searchMarkup}
            {recentProductsMarkup}
            {filterChipMarkup}
            {dividerMarkup}
          </View>
        )}
      />
    </Page>
  );
};
